import { api } from '../../shared/api';
import { useState, useRef } from 'react';
import { FileSpreadsheet, FileText, Upload, X, Download, AlertCircle, CheckCircle2, Plus } from 'lucide-react';

type Status = 'idle' | 'processing' | 'done' | 'error';

const MAX_FILES = 2;

export function PfFileGenPage() {
  // Each of these two slots accepts up to MAX_FILES files. Their rows are
  // concatenated server-side before corporate actions are applied.
  const [boFiles, setBoFiles] = useState<File[]>([]);
  const [caFiles, setCaFiles] = useState<File[]>([]);
  const [status,    setStatus]    = useState<Status>('idle');
  const [errorMsg,  setErrorMsg]  = useState<string | null>(null);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputName,setOutputName]= useState<string | null>(null);

  // ── Stage 2: compare generated BO+CA PF against an uploaded actual PF ──
  const [pfFile,        setPfFile]        = useState<File | null>(null);
  const [cmpStatus,     setCmpStatus]     = useState<Status>('idle');
  const [cmpErrorMsg,   setCmpErrorMsg]   = useState<string | null>(null);
  const [cmpOutputUrl,  setCmpOutputUrl]  = useState<string | null>(null);
  const [cmpOutputName, setCmpOutputName] = useState<string | null>(null);

  const boRef = useRef<HTMLInputElement>(null);
  const caRef = useRef<HTMLInputElement>(null);
  const pfRef = useRef<HTMLInputElement>(null);

  function resetCompare() {
    setCmpStatus('idle');
    setCmpErrorMsg(null);
    setCmpOutputUrl(null);
    setCmpOutputName(null);
  }

  async function handleCompare() {
    if (boFiles.length === 0 || caFiles.length === 0 || !pfFile) return;
    resetCompare();
    setCmpStatus('processing');
    try {
      const form = new FormData();
      boFiles.forEach(f => form.append('bo_file', f));
      caFiles.forEach(f => form.append('ca_file', f));
      form.append('pf_file', pfFile);

      const response = await api.post(
        '/api/pf-file-gen/compare-with-pf',
        form,
        { responseType: 'blob' },
      );

      const blob        = response.data as Blob;
      const url         = URL.createObjectURL(blob);
      const disposition = String(response.headers['content-disposition'] ?? '');
      const nameMatch   = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)/i);
      const outName     = nameMatch?.[1]
        ? decodeURIComponent(nameMatch[1])
        : `PF_comparison_${pfFile.name.replace(/\.[^.]+$/, '')}.xlsx`;

      setCmpOutputUrl(url);
      setCmpOutputName(outName);
      setCmpStatus('done');
    } catch (err: unknown) {
      let msg = 'Unknown error';
      const axiosErr = err as { response?: { data?: unknown }; message?: string };
      const data = axiosErr?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed?.error ?? text ?? msg;
        } catch { msg = axiosErr.message ?? msg; }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setCmpErrorMsg(msg);
      setCmpStatus('error');
    }
  }

  function reset() {
    setStatus('idle');
    setErrorMsg(null);
    setOutputUrl(null);
    setOutputName(null);
  }

  async function handleSubmit() {
    if (boFiles.length === 0 || caFiles.length === 0) return;
    reset();
    setStatus('processing');

    try {
      const form = new FormData();
      boFiles.forEach(f => form.append('bo_file', f));
      caFiles.forEach(f => form.append('ca_file', f));

      // Use the shared axios instance (same as recon) — it handles baseURL,
      // withCredentials, and the dev auth header consistently. Raw fetch had
      // intermittent 'Failed to fetch' issues with the multipart upload.
      const response = await api.post(
        '/api/pf-file-gen/apply-corporate-actions',
        form,
        { responseType: 'blob' },
      );

      const blob       = response.data as Blob;
      const url        = URL.createObjectURL(blob);
      const disposition = String(response.headers['content-disposition'] ?? '');
      const nameMatch  = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)/i);
      const outName    = nameMatch?.[1]
        ? decodeURIComponent(nameMatch[1])
        : `${boFiles[0].name.replace(/\.[^.]+$/, '')}_PF_adjusted.xlsx`;

      setOutputUrl(url);
      setOutputName(outName);
      setStatus('done');
    } catch (err: unknown) {
      // axios error responses for a blob responseType come back as a Blob —
      // read and parse it to extract the server's JSON error message.
      let msg = 'Unknown error';
      const axiosErr = err as { response?: { data?: unknown }; message?: string };
      const data = axiosErr?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed?.error ?? text ?? msg;
        } catch { msg = axiosErr.message ?? msg; }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrorMsg(msg);
      setStatus('error');
    }
  }

  const ready = boFiles.length > 0 && caFiles.length > 0 && status !== 'processing';
  const cmpReady = boFiles.length > 0 && caFiles.length > 0 && !!pfFile && cmpStatus !== 'processing';

  return (
    <div className="max-w-xl mx-auto px-4 py-10">

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-strong)' }}>
          PF File Generator
        </h1>
        <p className="mt-1.5 text-sm" style={{ color: 'var(--text-200)' }}>
          Upload your BO transaction file and the CMOTS CA master. Corporate-action
          adjustments will be applied and a PF file will be generated for download.
        </p>
      </div>

      {/* Upload card */}
      <div className="rounded-2xl p-6 space-y-5"
           style={{
             background: 'var(--bg-elevated)',
             border: '1px solid var(--glass-border)',
             boxShadow: 'inset 0 1px 0 0 var(--glass-edge)',
           }}>

        {/* BO Files (up to MAX_FILES) */}
        <MultiFileSlot
          label="BO Transaction File (.xlsx)"
          hint="Broker office file — ISIN, Trade Date, Buy/Sell, Qty, Price, Contract Note…"
          icon={<FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--accent)' }} />}
          files={boFiles}
          inputRef={boRef}
          onChange={f => { setBoFiles(f); reset(); }}
        />

        {/* CA Files (up to MAX_FILES) */}
        <MultiFileSlot
          label="CMOTS CA Master File (.xlsx)"
          hint="Corporate action master — ISIN, CorpType, Ex-date, Remark, To_ISIN…"
          icon={<FileText className="w-5 h-5" style={{ color: 'var(--accent)' }} />}
          files={caFiles}
          inputRef={caRef}
          onChange={f => { setCaFiles(f); reset(); }}
        />

        {/* Error */}
        {status === 'error' && errorMsg && (
          <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl text-sm"
               style={{
                 background: 'color-mix(in srgb, var(--status-err) 10%, transparent)',
                 border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)',
                 color: 'var(--status-err)',
               }}>
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          disabled={!ready}
          onClick={handleSubmit}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition"
          style={{
            background: ready
              ? 'linear-gradient(135deg, var(--accent) 0%, #e0336a 100%)'
              : 'var(--bg-hover)',
            color:  ready ? '#fff' : 'var(--text-400)',
            cursor: ready ? 'pointer' : 'not-allowed',
            boxShadow: ready ? '0 4px 14px 0 var(--accent-soft)' : 'none',
          }}
        >
          {status === 'processing' ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Applying corporate actions…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Apply corporate actions &amp; generate PF file
            </>
          )}
        </button>
      </div>

      {/* Output download card */}
      {status === 'done' && outputUrl && outputName && (
        <div className="mt-5 rounded-2xl p-5"
             style={{
               background: 'color-mix(in srgb, var(--status-ok) 8%, var(--bg-elevated))',
               border: '1px solid color-mix(in srgb, var(--status-ok) 35%, transparent)',
             }}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: 'color-mix(in srgb, var(--status-ok) 15%, transparent)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--status-ok)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--text-strong)' }}>
                {outputName}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--status-ok)' }}>
                PF file generated successfully
              </div>
            </div>
            <a
              href={outputUrl}
              download={outputName}
              className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium"
              style={{
                background: 'var(--status-ok)',
                color: '#fff',
                boxShadow: '0 2px 8px 0 rgba(0,180,100,0.25)',
                textDecoration: 'none',
              }}
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
          </div>
        </div>
      )}

      {/* ── Stage 2: Compare against uploaded PF ─────────────────────────── */}
      <div className="mt-8 mb-3">
        <h2 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--text-strong)' }}>
          Verify against actual PF
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--text-400)' }}>
          Upload the actual PF file. Every entry of the generated BO+CA PF will be
          checked against it — you'll get a workbook listing what's present and
          what's missing.
        </p>
      </div>

      <div className="rounded-2xl p-6 space-y-5"
           style={{
             background: 'var(--bg-elevated)',
             border: '1px solid var(--glass-border)',
             boxShadow: 'inset 0 1px 0 0 var(--glass-edge)',
           }}>

        {(boFiles.length === 0 || caFiles.length === 0) && (
          <div className="text-xs px-3 py-2 rounded-lg"
               style={{ background: 'var(--bg-surface)', color: 'var(--text-400)' }}>
            Select the BO and CMOTS files above first — the comparison regenerates
            the BO+CA PF from them, then checks it against your uploaded PF.
          </div>
        )}

        <FileSlot
          label="Actual PF File (.xlsx)"
          hint="PF-format file to verify against — standard columns, header row 11"
          icon={<FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--accent)' }} />}
          file={pfFile}
          inputRef={pfRef}
          onChange={f => { setPfFile(f); resetCompare(); }}
        />

        {cmpStatus === 'error' && cmpErrorMsg && (
          <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl text-sm"
               style={{
                 background: 'color-mix(in srgb, var(--status-err) 10%, transparent)',
                 border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)',
                 color: 'var(--status-err)',
               }}>
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{cmpErrorMsg}</span>
          </div>
        )}

        <button
          type="button"
          disabled={!cmpReady}
          onClick={handleCompare}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition"
          style={{
            background: cmpReady
              ? 'linear-gradient(135deg, var(--accent) 0%, #e0336a 100%)'
              : 'var(--bg-hover)',
            color:  cmpReady ? '#fff' : 'var(--text-400)',
            cursor: cmpReady ? 'pointer' : 'not-allowed',
            boxShadow: cmpReady ? '0 4px 14px 0 var(--accent-soft)' : 'none',
          }}
        >
          {cmpStatus === 'processing' ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Comparing…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Compare &amp; generate report
            </>
          )}
        </button>
      </div>

      {cmpStatus === 'done' && cmpOutputUrl && cmpOutputName && (
        <div className="mt-5 rounded-2xl p-5"
             style={{
               background: 'color-mix(in srgb, var(--status-ok) 8%, var(--bg-elevated))',
               border: '1px solid color-mix(in srgb, var(--status-ok) 35%, transparent)',
             }}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: 'color-mix(in srgb, var(--status-ok) 15%, transparent)' }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--status-ok)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--text-strong)' }}>
                {cmpOutputName}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--status-ok)' }}>
                Comparison report ready — Summary, Present, Not-Present
              </div>
            </div>
            <a
              href={cmpOutputUrl}
              download={cmpOutputName}
              className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium"
              style={{
                background: 'var(--status-ok)',
                color: '#fff',
                boxShadow: '0 2px 8px 0 rgba(0,180,100,0.25)',
                textDecoration: 'none',
              }}
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Multi-file slot (up to `max` files) ───────────────────────────────── */
function MultiFileSlot({
  label, hint, icon, files, inputRef, onChange, max = MAX_FILES,
}: {
  label:    string;
  hint:     string;
  icon:     React.ReactNode;
  files:    File[];
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (files: File[]) => void;
  max?:     number;
}) {
  const atCap = files.length >= max;

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const merged = [...files];
    for (const f of Array.from(list)) {
      if (merged.length >= max) break;
      // Skip exact duplicates (same name + size) so re-selecting is harmless.
      if (!merged.some(e => e.name === f.name && e.size === f.size)) merged.push(f);
    }
    onChange(merged.slice(0, max));
    if (inputRef.current) inputRef.current.value = '';
  }

  function removeAt(i: number) {
    onChange(files.filter((_, idx) => idx !== i));
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>{label}</div>
        <div className="text-[11px] tabular-nums" style={{ color: 'var(--text-400)' }}>
          {files.length}/{max}
        </div>
      </div>
      <div className="text-xs mb-2" style={{ color: 'var(--text-400)' }}>{hint}</div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="space-y-2 mb-2">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${file.size}-${i}`}
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{
                border: '1.5px solid var(--accent)',
                background: 'color-mix(in srgb, var(--accent) 6%, transparent)',
              }}
            >
              {icon}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: 'var(--text-strong)' }}>
                  {file.name}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-200)' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button
                type="button"
                onClick={e => { e.preventDefault(); removeAt(i); }}
                className="shrink-0 p-1 rounded-lg transition"
                style={{ color: 'var(--text-400)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--status-err)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-400)'; }}
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add-file control (hidden once the cap is reached) */}
      {!atCap ? (
        <label
          className="flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition"
          style={{
            border: `1.5px dashed ${files.length > 0 ? 'var(--glass-border-h)' : 'var(--glass-border-h)'}`,
            background: 'var(--bg-surface)',
          }}
        >
          {files.length > 0
            ? <Plus className="w-5 h-5 shrink-0" style={{ color: 'var(--accent)' }} />
            : icon}
          <span className="text-sm" style={{ color: 'var(--text-400)' }}>
            {files.length > 0
              ? `Add another file (up to ${max})`
              : 'Click to select .xlsx file(s)'}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            multiple
            className="hidden"
            onChange={e => addFiles(e.target.files)}
          />
        </label>
      ) : (
        <div className="text-[11px] px-1" style={{ color: 'var(--text-400)' }}>
          Maximum of {max} files reached.
        </div>
      )}
    </div>
  );
}

/* ── Reusable single file slot ─────────────────────────────────────────── */
function FileSlot({
  label, hint, icon, file, inputRef, onChange,
}: {
  label:    string;
  hint:     string;
  icon:     React.ReactNode;
  file:     File | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (f: File | null) => void;
}) {
  return (
    <div>
      <div className="text-sm font-medium mb-1.5" style={{ color: 'var(--text-strong)' }}>
        {label}
      </div>
      <div className="text-xs mb-2" style={{ color: 'var(--text-400)' }}>{hint}</div>

      <label
        className="flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition"
        style={{
          border: `1.5px dashed ${file ? 'var(--accent)' : 'var(--glass-border-h)'}`,
          background: file
            ? 'color-mix(in srgb, var(--accent) 6%, transparent)'
            : 'var(--bg-surface)',
        }}
      >
        {icon}

        {file ? (
          <div className="flex flex-1 items-center gap-3 min-w-0">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--text-strong)' }}>
                {file.name}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-200)' }}>
                {(file.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              type="button"
              onClick={e => { e.preventDefault(); onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
              className="shrink-0 p-1 rounded-lg transition"
              style={{ color: 'var(--text-400)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--status-err)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-400)'; }}
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <span className="text-sm" style={{ color: 'var(--text-400)' }}>
            Click to select a .xlsx file
          </span>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={e => onChange(e.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  );
}
